import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import Layout from '../components/layout/Layout';
import { CheckCircleIcon, ExclamationTriangleIcon, ClockIcon } from '../utils/IconWrapper';
import { getApiEndpoint } from '../utils/config';
import { supabase } from '../utils/supabaseClient';

const VerifyContainer = styled.div`
  max-width: 600px;
  margin: 0 auto;
  padding: 2rem;
  text-align: center;
  min-height: 60vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const StatusCard = styled(motion.div)<{ status: 'loading' | 'success' | 'error' }>`
  background: var(--bg-secondary);
  border-radius: 16px;
  padding: 3rem;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  border: 2px solid ${props => 
    props.status === 'success' ? '#004744' : 
    props.status === 'error' ? '#ef4444' : 
    'var(--border-color)'
  };
  width: 100%;
  max-width: 500px;
`;

const StatusIcon = styled.div<{ status: 'loading' | 'success' | 'error' }>`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 2rem;
  font-size: 2.5rem;
  background: ${props => 
    props.status === 'success' ? 'linear-gradient(135deg, #004744 0%, #004744 100%)' : 
    props.status === 'error' ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' : 
    'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)'
  };
  color: white;
  
  ${props => props.status === 'loading' && `
    animation: pulse 2s infinite;
    @keyframes pulse {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.5; }
    }
  `}
`;

const Title = styled.h1<{ status: 'loading' | 'success' | 'error' }>`
  font-size: 2rem;
  margin-bottom: 1rem;
  color: ${props => 
    props.status === 'success' ? '#004744' : 
    props.status === 'error' ? '#ef4444' : 
    'var(--text-primary)'
  };
`;

const Message = styled.p`
  font-size: 1.1rem;
  color: var(--text-secondary);
  line-height: 1.6;
  margin-bottom: 2rem;
`;

const ActionButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: linear-gradient(135deg, #004744 0%, #004744 100%);
  color: white;
  padding: 1rem 2rem;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.2s ease;
  margin: 0.5rem;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 71, 68, 0.4);
  }
`;

const SecondaryButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: var(--bg-primary);
  color: var(--text-primary);
  padding: 1rem 2rem;
  border-radius: 8px;
  border: 2px solid var(--border-color);
  text-decoration: none;
  font-weight: 500;
  font-size: 1rem;
  transition: all 0.2s ease;
  margin: 0.5rem;
  
  &:hover {
    border-color: #004744;
    background: rgba(0, 71, 68, 0.1);
  }
`;

const VerifyEmail: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('Verifica del token in corso...');
  
  useEffect(() => {
    const verifyEmailToken = async () => {
      // Controlla i parametri URL standard di Supabase
      const token_hash = searchParams.get('token_hash');
      const type = searchParams.get('type');
      
      // Gestione verifica email standard di Supabase
      if (token_hash && type === 'signup') {
        console.log('🎉 Verifica email con token Supabase:', token_hash);
        
        try {
          // Usa il metodo nativo di Supabase per verificare l'email
          const { data, error } = await supabase.auth.verifyOtp({
            token_hash,
            type: 'signup'
          });

          if (error) {
            console.error('Errore verifica Supabase:', error);
            setStatus('error');
            setMessage('Errore durante la verifica. Il link potrebbe essere scaduto.');
            return;
          }

          console.log('✅ Email verificata da Supabase:', data);
          setStatus('success');
          setMessage('Email verificata con successo! Il tuo account è ora attivo.');
          
          // Reindirizza automaticamente al login dopo 3 secondi
          setTimeout(() => {
            navigate('/login?verified=true');
          }, 3000);
          return;

        } catch (error) {
          console.error('Errore verifica email:', error);
          setStatus('error');
          setMessage('Errore di connessione durante la verifica.');
          return;
        }
      }

      // Controllo se è il redirect da Supabase dopo verifica (metodo custom)
      const confirmed = searchParams.get('confirmed');
      if (confirmed === 'true') {
        console.log('🎉 Redirect da Supabase - email verificata!');
        setStatus('success');
        setMessage('Email verificata con successo! Il tuo account è ora attivo.');
        
        // Reindirizza automaticamente al login dopo 3 secondi
        setTimeout(() => {
          navigate('/login?verified=true');
        }, 3000);
        return;
      }

      // Gestione del token legacy (per compatibilità)
      const token = searchParams.get('token');
      if (!token && !token_hash) {
        setStatus('error');
        setMessage('Link di verifica non valido. Controlla che il link sia corretto.');
        return;
      }

      if (token && (token === 'undefined' || token === 'null')) {
        setStatus('error');
        setMessage('Token di verifica non valido. Controlla che il link sia corretto.');
        return;
      }

      // Sistema legacy per token personalizzati (mantenuto per compatibilità)
      if (token) {
        try {
          const response = await fetch(getApiEndpoint('/api/verify-email-token'), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ token }),
          });

          const result = await response.json();

          if (response.ok && result.success) {
            setStatus('success');
            setMessage('Email verificata con successo! Adesso puoi accedere al tuo account.');
            
            // Reindirizza automaticamente al login dopo 3 secondi
            setTimeout(() => {
              navigate('/login?verified=true');
            }, 3000);
          } else {
            setStatus('error');
            const errorMessage = result.error || 'Token non valido o scaduto. Il link potrebbe essere già stato utilizzato.';
            
            // Controlla se è un errore di email già verificata
            if (errorMessage.includes('già verificata')) {
              setMessage('Questa email è già stata verificata in precedenza. Puoi procedere direttamente al login.');
            } else {
              setMessage(errorMessage);
            }
          }
        } catch (error) {
          console.error('Errore verifica email:', error);
          setStatus('error');
          setMessage('Errore di connessione. Riprova più tardi.');
        }
      }
    };

    // Ritarda di 1 secondo per dare tempo al loading animation
    const timer = setTimeout(verifyEmailToken, 1000);
    return () => clearTimeout(timer);
  }, [searchParams, navigate]);

  const getStatusIcon = () => {
    switch (status) {
      case 'success':
        return <CheckCircleIcon />;
      case 'error':
        return <ExclamationTriangleIcon />;
      default:
        return <ClockIcon />;
    }
  };

  const getTitle = () => {
    switch (status) {
      case 'success':
        return 'Email Verificata! 🎉';
      case 'error':
        return 'Verifica Fallita';
      default:
        return 'Verifica in Corso...';
    }
  };

  return (
    <Layout>
      <VerifyContainer>
        <StatusCard 
          status={status}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <StatusIcon status={status}>
            {getStatusIcon()}
          </StatusIcon>
          
          <Title status={status}>
            {getTitle()}
          </Title>
          
          <Message>
            {message}
          </Message>
          
          {status === 'success' && (
            <div>
              <Message style={{ fontSize: '0.9rem', marginBottom: '1rem', color: '#004744' }}>
                Sarai reindirizzato automaticamente alla pagina di login tra pochi secondi...
              </Message>
              <ActionButton to="/login?verified=true">
                🔐 Accedi Ora
              </ActionButton>
              <SecondaryButton to="/accommodations">
                🏡 Esplora Alloggi
              </SecondaryButton>
            </div>
          )}
          
          {status === 'error' && (
            <div>
              {message.includes('già stata verificata') ? (
                // Pulsanti per email già verificata
                <>
                  <ActionButton to="/login">
                    🔐 Vai al Login
                  </ActionButton>
                  <SecondaryButton to="/contact">
                    📞 Contatta Supporto
                  </SecondaryButton>
                </>
              ) : (
                // Pulsanti per altri errori
                <>
                  <ActionButton to="/register">
                    🔄 Registrati di Nuovo
                  </ActionButton>
                  <SecondaryButton to="/contact">
                    📞 Contatta Supporto
                  </SecondaryButton>
                </>
              )}
            </div>
          )}
          
          {status === 'loading' && (
            <Message style={{ fontSize: '0.9rem', opacity: 0.7 }}>
              Ti preghiamo di attendere...
            </Message>
          )}
        </StatusCard>
      </VerifyContainer>
    </Layout>
  );
};

export default VerifyEmail; 